import { trackEvent } from "@/lib/analytics";
/**
 * Beta Privada — puerta única de acceso.
 *
 * Mientras `app_settings.waitlist_enabled` esté activo, ningún flujo de pago
 * (Club Pro, Mentoring, add-ons) abre Stripe: la solicitud se registra en la
 * lista de espera con el servicio que el usuario quería, para poder priorizar
 * invitaciones desde el Back Office.
 */
import { getSupabase } from "@/lib/supabase-lazy";

export type BetaPlan = "sin_plan" | "lite" | "basico" | "club_pro" | "completo" | "mentoring" | (string & {});

let cachedFlag: Promise<boolean> | undefined;

/** ¿Está activa la Beta Privada? (cacheado en memoria por sesión de pestaña) */
export function isBetaActive(): Promise<boolean> {
  if (!cachedFlag) {
    cachedFlag = (async () => {
      try {
        const supabase = await getSupabase();
        const { data } = await supabase
          .from("app_settings")
          .select("value")
          .eq("key", "waitlist_enabled")
          .maybeSingle();
        const val = data?.value as unknown;
        return val === true || val === "true";
      } catch {
        return false;
      }
    })();
  }
  return cachedFlag;
}

/** Traduce un product_type de checkout al servicio solicitado de la waitlist. */
export function planFromProductType(productType?: string | null, productKey?: string | null): BetaPlan {
  const type = (productType ?? "").toLowerCase();
  const key = (productKey ?? "").toLowerCase();
  if (!type || type === "free") return "sin_plan";
  if (type === "lite") return "lite";
  if (type === "plan" && ["basico", "club_pro", "completo"].includes(key)) return key as BetaPlan;
  if (type === "pro" || type === "panel" || type === "panel_pro") return "club_pro";
  if (type.startsWith("vip") || type.includes("mentoring")) return "mentoring";
  if (type === "addon" || type === "module") return `addon:${productKey ?? "unknown"}`;
  return "club_pro";
}

/** Servicio solicitado guardado por un CTA de pago antes del registro. */
export function pendingCheckoutPlan(): { plan: BetaPlan; interval: string | null } {
  try {
    const raw = typeof window !== "undefined" ? localStorage.getItem("pending_checkout") : null;
    if (!raw) return { plan: "sin_plan", interval: null };
    const p = JSON.parse(raw) as { product_type?: string; product_key?: string; billing_interval?: string };
    return {
      plan: planFromProductType(p.product_type, p.product_key),
      interval: p.billing_interval ?? null,
    };
  } catch {
    return { plan: "sin_plan", interval: null };
  }
}

export interface JoinBetaArgs {
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  plan?: BetaPlan;
  interval?: string | null;
  source?: string;
}

/** Alta idempotente en la lista de espera (nunca degrada el servicio pedido). */
export async function joinBetaWaitlist(args: JoinBetaArgs): Promise<"created" | "updated" | "error"> {
  try {
    const supabase = await getSupabase();
    // Visitante (rl_vid) y visita actuales, para saber de dónde vino; null sin consentimiento.
    let sessionKey: string | null = null;
    let visitId: string | null = null;
    try {
      const pa = await import("@/lib/publicActivity");
      sessionKey = pa.getPublicSessionKey() || null;
      visitId = pa.getVisitId() || null;
    } catch { /* sin origen */ }
    const { data, error } = await supabase.rpc("waitlist_join_v2" as never, {
      _email: args.email.trim().toLowerCase(),
      _first_name: args.firstName ?? null,
      _last_name: args.lastName ?? null,
      _requested_plan: args.plan ?? "sin_plan",
      _requested_interval: args.interval ?? null,
      _source: args.source ?? "manual",
      _session_key: sessionKey,
      _visit_id: visitId,
    } as never);
    if (error) return "error";
    const res = (data as unknown as "created" | "updated") ?? "updated";
    if (res === "created") {
      trackEvent("join_waitlist", { plan: args.plan, billing_interval: args.interval ?? undefined, waitlist_source: args.source });
      (await import("@/lib/captureChannel")).clearConsumedChannel();
    }
    return res;
  } catch {
    return "error";
  }
}

/**
 * Intercepta un checkout durante la Beta Privada.
 *
 * Devuelve `true` cuando la solicitud se ha registrado y el checkout debe
 * abortarse; `false` cuando el pago puede continuar (Beta apagada, admin o
 * cliente con suscripción ya activa).
 */
export async function betaGateCheckout(opts: {
  plan: BetaPlan;
  interval?: string | null;
  lang?: "es" | "en";
}): Promise<boolean> {
  if (!(await isBetaActive())) return false;

  const lang = opts.lang === "en" ? "en" : "es";
  const supabase = await getSupabase();
  const { data: { session } } = await supabase.auth.getSession();
  const user = session?.user;

  if (user) {
    // Admins y clientes con servicio activo siguen pudiendo pagar/gestionar.
    const [{ data: isAdmin }, { data: isRo }] = await Promise.all([
      supabase.rpc("has_role", { _user_id: user.id, _role: "admin" as never }),
      supabase.rpc("has_role", { _user_id: user.id, _role: "admin_lectura" as never }),
    ]);
    if (isAdmin === true || isRo === true) return false;

    const { data: subs } = await supabase
      .from("user_module_subscriptions")
      .select("id")
      .eq("user_id", user.id)
      .eq("status", "active")
      .limit(1);
    if (subs && subs.length > 0) return false;

    const { data: profile } = await supabase
      .from("profiles")
      .select("first_name, last_name")
      .eq("user_id", user.id)
      .maybeSingle();

    await joinBetaWaitlist({
      email: user.email ?? "",
      firstName: profile?.first_name ?? null,
      lastName: profile?.last_name ?? null,
      plan: opts.plan,
      interval: opts.interval ?? null,
      source: (await import("@/lib/captureChannel")).waitlistSourceFor("in_app"),
    });
  }

  const { toast } = await import("sonner");
  if (user) {
    toast.success(
      lang === "es"
        ? "Solicitud registrada 🍋 El Panel está en Beta Privada: te avisaremos por email en cuanto tengamos plaza para ti."
        : "Request received 🍋 The Panel is in Private Beta: we'll email you as soon as we have a spot for you.",
      { duration: 8000 },
    );
  } else {
    toast.info(
      lang === "es"
        ? "El Panel está en Beta Privada. Pide tu plaza y te avisaremos cuando abramos nuevas invitaciones."
        : "The Panel is in Private Beta. Request your spot and we'll notify you when new invitations open.",
      { duration: 8000 },
    );
  }
  return true;
}
